
import { defineComponent, onMounted, ref, computed } from "vue";
import { useField, useForm } from "vee-validate";
import { useRoute } from "vue-router";
import { KhoService } from "@/core/services/Kho.service";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { useLoading } from "@/core/hooks/use-loading";
import {
  getErrorMsg,
  swalErrNotification,
  swalNotification,
} from "@/core/helpers/utils";
import { useButtonCustom } from "../../../../core/hooks/use-button-custom";
import { ITaiKhoanResponseBase } from "../../../../core/interfaces/ApiResponses";

import * as Yup from "yup";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import TaiKhoanSelectionV2 from "@/components/shared/tai-khoan-selection/TaiKhoanSelectionV2.vue";

export default defineComponent({
  name: "update-warehouse",

  components: {
    ButtonCustom,
    PageContentBase,
    DivRow,
    DivCol,
    LabelCustom,
    TaiKhoanSelectionV2,
  },

  setup() {
    const route = useRoute();
    const { goBack, replace } = useRouterCustom("warehouseId");
    const { startLoading, endLoading } = useLoading();
    const { ButtonsType, ButtonTypeColors, ButtonTypes } = useButtonCustom();

    const warehouseId = computed(() => Number(route.params.warehouseId));

    const tenKhoInputRef = ref<{ focus: () => void } | null>(null);
    const isWarehouseLoaded = ref(false);

    const taiKhoanData = ref<{
      id: number;
      maTaiKhoan: string;
      maChiTiet?: string;
      tenTaiKhoan: string;
    }>({
      id: 0,
      maChiTiet: "",
      maTaiKhoan: "",
      tenTaiKhoan: "",
    });

    const updateWarehouseValidator = Yup.object({
      name: Yup.string().required("Tên kho là bắt buộc"),
      note: Yup.string().nullable(),
      address: Yup.string().required("Địa chỉ là bắt buộc"),
    });

    const { validate, setValues } = useForm({
      validationSchema: updateWarehouseValidator,
    });

    const { value: name, errorMessage: nameErrorMess } =
      useField<string>("name");
    const { value: code } = useField<string>("code");
    const { value: note } = useField<string>("note");
    const { value: address, errorMessage: addressErrorMess } =
      useField<string>("address");

    onMounted(async () => {
      startLoading();

      setBreadcrumbLinks([
        {
          text: "Kho",
          routeData: {
            name: "danh-sach-kho",
          },
        },
        {
          text: "Chi tiết",
          routeData: {
            name: "chi-tiet-kho",
            params: {
              id: warehouseId.value,
            },
          },
        },
        {
          text: "Cập nhật",
        },
      ]);

      try {
        const {
          data: { data: warehouseRes },
        } = await KhoService.fetchWarehouseDetailById(
          warehouseId.value.toString()
        );

        setValues({
          name: warehouseRes.ten_kho || "",
          code: warehouseRes.ma_kho || "",
          address: warehouseRes.dia_chi || "",
          note: warehouseRes.ghi_chu || "",
        });

        taiKhoanData.value.maTaiKhoan = warehouseRes.tk_ketoan?.matk || "";
        taiKhoanData.value.maChiTiet = warehouseRes.tk_ketoan?.mact || "";
        taiKhoanData.value.tenTaiKhoan = warehouseRes.tk_ketoan?.tentk || "";
        taiKhoanData.value.id = warehouseRes.tk_ketoan?.id || 0;
        isWarehouseLoaded.value = true;

        setCurrentPageTitle(`${name.value} (${code.value})`);
        document.title = `${name.value} (${code.value}) - Cập nhật - Kho - Đường sắt Hà Nội`;

        endLoading();
        tenKhoInputRef.value?.focus();
      } catch (error) {
        endLoading();

        await swalNotification(
          getErrorMsg(error, "Có lỗi khi lấy thông tin kho"),
          "error"
        );
      }
    });

    const onSubmit = async () => {
      if (!taiKhoanData.value.maTaiKhoan || !taiKhoanData.value.maChiTiet) {
        swalErrNotification(null, "Bắt buộc phải nhập đủ thông tin tài khoản");

        return;
      }

      const { valid, errors } = await validate();

      if (!valid) {
        const firstError = Object.values(errors)[0];
        await swalErrNotification(
          null,
          firstError || "Vui lòng kiểm tra lại thông tin form"
        );

        return;
      }

      try {
        startLoading();
        await KhoService.updateWarehouse({
          ten: name.value,
          dia_chi: address.value,
          ma_kho: code.value,
          ghi_chu: note.value || "",
          id: warehouseId.value,
          id_tai_khoan: taiKhoanData.value.id,
          ma_chi_tiet: taiKhoanData.value.maChiTiet,
          ma_tai_khoan: taiKhoanData.value.maTaiKhoan,
          ten_tai_khoan: taiKhoanData.value.tenTaiKhoan,
        });

        endLoading();
        await replace(`/quan-ly/kho/chi-tiet/${warehouseId.value}`);
      } catch (error) {
        endLoading();
        await swalNotification(
          getErrorMsg(error, "Có lỗi khi cập nhật thông tin kho"),
          "error"
        );
      }
    };

    const onSelectTaiKhoan = ({
      taiKhoanItem: {
        id: id_tai_khoan,
        ma_tai_khoan,
        ma_chi_tiet,
        ten_tai_khoan,
      },
    }: {
      taiKhoanItem: ITaiKhoanResponseBase;
    }) => {
      taiKhoanData.value.id = id_tai_khoan;
      taiKhoanData.value.maTaiKhoan = ma_tai_khoan;
      taiKhoanData.value.tenTaiKhoan = ten_tai_khoan;
      taiKhoanData.value.maChiTiet = ma_chi_tiet ? ma_chi_tiet.toString() : "";
    };

    return {
      ButtonsType,
      ButtonTypeColors,
      ButtonTypes,
      onSubmit,
      tenKhoInputRef,
      name,
      nameErrorMess,
      code,
      note,
      address,
      addressErrorMess,
      goBack,
      isWarehouseLoaded,
      taiKhoanData,
      onSelectTaiKhoan,
    };
  },
});
